import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://ballotr.com';
const PAGE_TITLE = 'Privacy Policy | Ballotr';
const PAGE_DESCRIPTION =
  'Read how Ballotr collects, uses, protects, and reports aggregated civic engagement data, including account, activity, and technical information.';
const PAGE_IMAGE = `${SITE_URL}/logo192.png`;

const sections = [
  {
    title: '1. Information We Collect',
    body: ['When users create accounts or interact with the platform, Ballotr may collect:'],
    groups: [
      {
        heading: 'Account Information',
        items: [
          'Name',
          'Email address',
          'Date of birth',
          'Gender',
          'Postal code',
          'Political party preference (optional)',
        ],
      },
      {
        heading: 'Platform Activity',
        items: [
          'Vote history',
          'Comments',
          'Campaign engagement',
          'Contribution intent signals',
          'User-reported completion',
        ],
      },
      {
        heading: 'Technical Information',
        items: [
          'IP address',
          'Browser type',
          'Device information',
          'Cookies used for authentication and security',
        ],
      },
      {
        heading: 'Derived Geographic Information',
        items: [
          'State derived from postal code',
          'County derived from postal code',
          'Congressional district (where available)',
          'Metropolitan area classification (where available)',
        ],
      },
    ],
    footer: [
      'When users provide a postal code during account creation, Ballotr may derive general geographic attributes such as state or county to improve platform functionality and generate aggregated civic insights.',
      'Ballotr does not collect GPS location data or real-time device location.',
    ],
  },
  {
    title: '2. How We Use Information',
    bullets: [
      'Operate the Ballotr platform',
      'Maintain account functionality',
      'Detect abuse or fraud',
      'Improve platform features',
      'Analyze civic engagement trends',
      'Generate aggregated civic insight reports',
    ],
  },
  {
    title: '3. Aggregated Civic Insights',
    body: [
      'Ballotr may analyze anonymized platform activity to generate insights about civic engagement and policy sentiment.',
      'These insights may be shared with organizations, researchers, journalists, or partners.',
      'Ballotr does not sell personal user data.',
      'Insights are generated only from anonymized and aggregated data.',
    ],
  },
  {
    title: '4. Privacy Safeguards for Aggregated Insights',
    body: [
      'To protect user privacy, Ballotr applies safeguards before generating civic insight reports.',
    ],
    bullets: [
      'Anonymization techniques',
      'Minimum group size thresholds before publishing data',
      'Aggregation across geographic or demographic groups',
    ],
    footer: [
      'These safeguards help ensure that insights cannot identify individual users.',
      'Insights reflect engagement trends among Ballotr users and are not intended to represent a scientific poll of the U.S. population.',
    ],
  },
  {
    title: '5. Geographic Insight Reporting',
    body: [
      'Ballotr may publish or share aggregated civic engagement insights based on geographic trends such as engagement levels by state, county, or region.',
      'These reports are generated exclusively from aggregated user activity and do not reveal the identity, actions, or preferences of individual users.',
      'Geographic insight reports are intended for informational and research purposes only and should not be interpreted as election predictions, polling results, or representative samples of the broader population.',
    ],
  },
  {
    title: '6. Cookies and Authentication',
    body: [
      'Ballotr uses cookies and similar technologies to maintain login sessions, ensure platform security, and improve user experience.',
      'Users may adjust browser settings to manage cookies.',
    ],
  },
  {
    title: '7. Third-Party Services',
    body: [
      'Ballotr may redirect users to third-party websites to complete donations or access related services.',
      'These services operate under their own policies and privacy practices. Ballotr does not process donations directly.',
    ],
  },
  {
    title: '8. Data Security',
    body: [
      'Ballotr uses reasonable security measures to protect user data.',
      'However, no system can guarantee absolute security.',
    ],
  },
  {
    title: '9. Data Retention',
    body: [
      'User information may be retained as long as necessary to operate the platform and comply with legal obligations.',
    ],
  },
  {
    title: '10. Data Deletion Requests',
    body: [
      'Users may request deletion of their Ballotr account and associated personal data by contacting Ballotr support at support@ballotr.com.',
      'Deletion requests should include the email address associated with the Ballotr account so we can verify and process the request.',
      'When an account deletion request is completed, Ballotr deletes or anonymizes associated personal profile data unless retention is required for legal, security, fraud prevention, or compliance reasons.',
      'Users may also visit https://ballotr.com/delete-account for account deletion instructions.',
    ],
  },
  {
    title: '11. Geographic Scope',
    body: ['Ballotr is intended for users located in the United States.'],
  },
  {
    title: '12. Changes to This Policy',
    body: [
      'Ballotr may update this Privacy Policy from time to time.',
      'Updated versions will be posted on the platform.',
    ],
  },
  {
    title: '13. Contact Information',
    body: ['Privacy inquiries may be directed to:'],
    contact: ['Bay View Concepts LLC', 'support@ballotr.com'],
  },
];

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`${SITE_URL}/privacy`} />
        <meta property="og:site_name" content="Ballotr" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}/privacy`} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-gradient-to-r from-sky-50 to-white px-6 py-8 sm:px-8">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-sky-700">
                Ballotr Legal
              </p>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Privacy Policy
              </h1>
              <p className="mt-3 text-sm text-slate-600">Effective Date: March 13, 2026</p>
            </div>

            <div className="px-6 py-8 sm:px-8">
              <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-base leading-7 text-slate-700">
                  This Privacy Policy explains how Ballotr collects and uses user information.
                </p>
                <p className="mt-4 text-base leading-7 text-slate-700">
                  Ballotr is operated by <span className="font-semibold">Bay View Concepts LLC</span>.
                </p>
              </div>

              <div className="space-y-8">
                {sections.map((section) => (
                  <section key={section.title}>
                    <h2 className="text-xl font-semibold text-slate-900">{section.title}</h2>

                    {section.body?.map((paragraph) => (
                      <p key={paragraph} className="mt-3 text-base leading-7 text-slate-700">
                        {paragraph}
                      </p>
                    ))}

                    {section.groups?.map((group) => (
                      <div
                        key={group.heading}
                        className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">
                          {group.heading}
                        </h3>
                        <ul className="mt-3 space-y-2 pl-5 text-base leading-7 text-slate-700">
                          {group.items.map((item) => (
                            <li key={item} className="list-disc">
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    {section.bullets && (
                      <ul className="mt-4 space-y-2 pl-5 text-base leading-7 text-slate-700">
                        {section.bullets.map((bullet) => (
                          <li key={bullet} className="list-disc">
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.footer?.map((paragraph) => (
                      <p key={paragraph} className="mt-3 text-base leading-7 text-slate-700">
                        {paragraph}
                      </p>
                    ))}

                    {section.contact && (
                      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-700">
                        {section.contact.map((line) => (
                          <p key={line} className="leading-7">
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;